import { min, startCase, uniq } from 'lodash';
import {
	CellEditorDataTransformer,
	ColumnModeTypeEnum,
	focusSuperGridCell,
	getLevelByDepth,
	getLevelDepth,
	insertAddColumns,
	MutationsType,
	NumberedAnchorsBody,
	NumberedAnchorsContainer,
	refocusCell,
	SuperGridVerticalAnchors,
	TransformedSuperGridColDef,
	TransformedVerticalSuperGridColDef,
	useColumnEditor,
	useSupergridMutations,
	useSuperGridValidate,
	useSupergridVerticalEngine,
	verticalParentNameColSpan,
	VerticalSuperGridColDef,
} from '@aleph/supergrid';
import {
	MAPPED_COLUMN_NAMES, RedditLevel, isPendingAdAccount,
} from 'wb-reddit/app/routes/superGrid/utils';
import React, {
	useEffect, useMemo, useState,
} from 'react';
import { REDDIT_SUPERGRID_CONFIG } from 'wb-reddit/app/routes/superGrid/config';
import { REDDIT_CROSS_LEVEL_DEPENDENCIES, RedditCreateLevels, RedditSuperGridMutationsConfig } from 'wb-reddit/app/routes/superGrid/mutator/utils';
import classNames from 'classnames';
import RedditVerticalAddCellRenderer from 'wb-reddit/app/routes/superGrid/mutator/vertical/cellRenderer/add';
import { AnimatePresence, motion } from 'framer-motion';
import { useApolloClient } from '@apollo/client';
import RedditMockedPreview from 'wb-reddit/app/routes/superGrid/cellRenderers/tool/mockedPreview';
import RedditCellRendererWrapper from 'wb-reddit/app/routes/superGrid/cellRenderers/wrapper';
import { adHeaderNames, groupHeaderNames } from 'wb-reddit/app/routes/superGrid/columnDefinitions/headerNames';
import { getExtraCreativeContent } from 'wb-reddit/app/routes/utils';
import {
	ArrowLeft,
	DirectionRight,
	processValueFromJSON,
	processValueToJSON,
	updateAndReturnAffectedChildren,
	Button, ButtonAppearance, ButtonColor, Floaty, Tooltip, TrashCanIcon, useUrlParams,
	ButtonSize,
	DefaultCellRenderer,
} from '@aleph/shared-components';
import { ErrorBoundary } from '@sentry/react';
import { preValidateRedditEntities, validateRedditEntity } from 'wb-reddit/app/routes/superGrid/validation/utils';
import { editableOnThisLevel } from 'wb-reddit/app/routes/superGrid/helpers/helpers';
import useMutationsUndoRedoPlugin from 'wb-reddit/app/routes/superGrid/hooks/useMutationsUndoRedoPlugin';
import {
	ColumnApi,
	GridApi,
	CellClassParams,
	GridReadyEvent,
	CellValueChangedEvent,
	CellKeyDownEvent,
	RowHeightParams,
	CellEditingStartedEvent,
	FillOperationParams,
	FillEndEvent,
	IRowNode,
} from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import useInstantEditPlugin from 'wb-reddit/app/routes/superGrid/hooks/useInstantEditPlugin';
import {
	changeMutationWithAutoName, handleCellLoading, isNamingFormula, transformNamingExpression,
} from 'wb-reddit/app/common/components/autoNaming/promptHelper';
import NameEditor from 'wb-reddit/src/app/common/components/autoNaming/autoNamingEditor';
import VerticalHeadlines from 'wb-reddit/app/routes/superGrid/mutator/vertical/headlines';
import { useNavigate, useParams } from 'react-router-dom';
import VerticalNameCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/name/verticalNameCellRenderer';
import { ValidationNavigator } from 'wb-spotify/app/common/supergridValidation';

import '@aleph/shared-components/scss/supergrid-mutator-vertical-grid.scss';
import { useTranslation } from 'react-i18next';

LicenseManager.setLicenseKey(
	// eslint-disable-next-line max-len
	'Using_this_AG_Grid_Enterprise_key_( AG-043155 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Httpool d.o.o. )_is_granted_a_( Multiple Applications )_Developer_License_for_( 3 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 1 July 2024 )____[v2]_MTcxOTc4ODQwMDAwMA==3f4d73dbd0b55114fae1030bca4daf44',
);

const DEFAULT_VERTICAL_COL_DEF = {
	width:     375,
	cellStyle: ({
		borderRightColor: 'hsl(var(--gray-300))',
		padding:          0,
	}),
};

const campaignVerticalNameColDef: TransformedVerticalSuperGridColDef<RedditLevel> = {
	field:            'campaignVerticalName',
	fieldName:        'Campaign',
	level:            RedditLevel.GROUP,
	columnMode:       ColumnModeTypeEnum.VERTICAL,
	cellRenderer:     DefaultCellRenderer,
	valueGetter:      params => params?.data.parent?.name,
	colSpan:          verticalParentNameColSpan,
	columnOrderIndex: -2,
};

const groupVerticalNameColDef: TransformedVerticalSuperGridColDef<RedditLevel> = {
	field:            'groupVerticalName',
	fieldName:        'Group',
	headerName:       groupHeaderNames.groupName,
	level:            RedditLevel.AD,
	columnMode:       ColumnModeTypeEnum.VERTICAL,
	cellRenderer:     DefaultCellRenderer,
	valueGetter:      params => params?.data.parent?.name,
	colSpan:          verticalParentNameColSpan,
	columnOrderIndex: -2,
};

const adPreviewColDef: TransformedSuperGridColDef<RedditLevel> = {
	colId:            'adFirst',
	level:            RedditLevel.AD,
	columnMode:       ColumnModeTypeEnum.VERTICAL,
	field:            'adPreview',
	headerName:       adHeaderNames.adPreview,
	columnOrderIndex: -2,
	cellRenderer:     params => (
		<div className="w-full h-full py-[8px] flex items-center justify-center">
			<ErrorBoundary
				fallback={<div>Couldn't generate preview</div>}
			>
				<RedditMockedPreview
					{...params}
				/>
			</ErrorBoundary>
		</div>
	),
	cellRendererParams: (params) => ({
		adAccount:             params.data?.parent?.parent?.parent?.adAccount,
		creativeAllowComments: params?.data?.creativeAllowComments,
		creativeHeadline:      params?.data?.creativeHeadline,
		creativeType:          params?.data?.creativeType,
		creativeThumbnailUrl:  params?.data?.creativeThumbnailUrl,
		creativeContent:       getExtraCreativeContent(params).filter(content => !!content.creativeContentMediaUrl),
		insideCell:            true,
	}),
};

const getVerticalLevel = (mutations: MutationsType<RedditSuperGridMutationsConfig>) => {
	if (mutations[RedditLevel.CAMPAIGN]?.some(campaign => !campaign?.id)) {
		return RedditLevel.CAMPAIGN;
	}

	if (mutations[RedditLevel.GROUP]?.some(group => !group?.id)) {
		return RedditLevel.GROUP;
	}

	return RedditLevel.AD;
};

const RedditVerticalMutationGrid = ({ confirmMutations }) => {
	const {
		mutations: storeMutations,
		changeMutation,
		getMutationsWithParents,
		clearMutations,
	} = useSupergridMutations<RedditSuperGridMutationsConfig>();
	const navigate = useNavigate();

	const { params: urlParams, setUrlParam } = useUrlParams({ navigate, useParams });
	const [visitedLevels, setVisitedLevels] = useState<RedditLevel[]>([]);

	const mutations = useMemo(() => getMutationsWithParents(REDDIT_SUPERGRID_CONFIG), [storeMutations]);

	const { context: { setValidations } } = useSuperGridValidate();

	const client = useApolloClient();

	const level = urlParams.creationLevel ?? getVerticalLevel(mutations);

	const { t } = useTranslation();

	useEffect(() => {
		setVisitedLevels(uniq([...visitedLevels, level]));
	}, [level]);

	useEffect(() => {
		preValidateRedditEntities({
			campaign: visitedLevels.includes(RedditLevel.CAMPAIGN) ? mutations.campaign : [],
			group:    visitedLevels.includes(RedditLevel.GROUP) ? mutations.group : [],
			ad:       visitedLevels.includes(RedditLevel.AD) ? mutations.ad : [],
		}).then(errors => setValidations(errors));
	}, [storeMutations, JSON.stringify(visitedLevels)]);

	const setLevel = (newLevel) => {
		setUrlParam('creationLevel', newLevel);
	};

	const [showHeader, setShowHeader] = useState<boolean>(true);
	const [colApi, setColApi] = useState<ColumnApi | null>(null);
	const [api, setApi] = useState<GridApi | null>(null);

	const { onCellEditingStarted: onCellEditingStartedColumnEditor, onCellEditingStopped } = useColumnEditor(
		{
			gridApi:  api,
			vertical: true,
		},
	);

	const { context } = useSuperGridValidate({
		validate:            validateRedditEntity,
		entityHasValidation: (v, e) => v.id === e.data?.id || v.id === e.data?.hash,
		gridApi:             api,
	});

	useEffect(() => {
		const handler = async (e: FillEndEvent) => {
			const changedColumns = e.finalRange.columns.filter(
				(finalCol) => !e.initialRange.columns.find(
					(initialCol) => initialCol.getColId() === finalCol.getColId(),
				),
			);

			const { namingExpression } = (e.initialRange.startColumn.getColDef() as VerticalSuperGridColDef).entityData;

			if (!isNamingFormula(namingExpression)) {
				return;
			}

			const changedRowNodes: IRowNode[] = [];

			e.api.forEachNode(node => {
				if (
					node.data?.field === 'name'
                    && changedRowNodes.length === 0
				) {
					changedRowNodes.push(node);
				}
			});

			handleCellLoading(changedColumns, changedRowNodes, true);

			let newNames: string[] | undefined;

			try {
				newNames = await transformNamingExpression(changedColumns.map((column) => ({
					data:       (column.getColDef() as VerticalSuperGridColDef).entityData,
					expression: namingExpression,
				})), client);
			} catch (err: unknown) {
				console.error(err);

				newNames = Array(changedColumns.length).fill("Error: Couldn't generate name");
			}
			newNames?.forEach((newName, index) => {
				const column = changedColumns[index];
				const data = (column.getColDef() as VerticalSuperGridColDef).entityData;
				changeMutationWithAutoName(data ?? {}, newName, data?.name, true, changeMutation);
			});

			handleCellLoading(changedColumns, changedRowNodes, false);
		};
		api?.addEventListener('fillEnd', handler);

		return () => {
			api?.removeEventListener('fillEnd', handler);
		};
	}, [api]);

	const validations = context?.validations ?? [];

	const onAnchorLevelChange = (newLevel: RedditLevel) => {
		api?.stopEditing();

		setLevel(newLevel);
	};

	useEffect(() => {
		onCellEditingStopped();
	}, [level]);

	const onPreviousLevel = () => {
		api?.stopEditing();

		switch (level) {
			case RedditLevel.GROUP:
				setLevel(RedditLevel.CAMPAIGN);

				break;
			case RedditLevel.AD:
				setLevel(RedditLevel.GROUP);

				break;
		}
	};

	const onNextLevel = async () => {
		api?.stopEditing();

		switch (level) {
			case RedditLevel.CAMPAIGN:
				setLevel(RedditLevel.GROUP);

				break;
			case RedditLevel.GROUP:
				setLevel(RedditLevel.AD);

				break;
			case RedditLevel.AD:
				confirmMutations(api);
		}
	};

	const config = useMemo(() => ({
		...REDDIT_SUPERGRID_CONFIG,
		cellRendererWrapper: RedditCellRendererWrapper,
		cellEditorWrapper:   CellEditorDataTransformer,
	}), [REDDIT_SUPERGRID_CONFIG]);

	const colDefs = useMemo(() => ([
		campaignVerticalNameColDef,
		groupVerticalNameColDef,
		adPreviewColDef,
		{
			field:              'name',
			colId:              `${level}Name`,
			headerName:         groupHeaderNames.groupName,
			cellRenderer:       RedditCellRendererWrapper,
			cellEditor:         NameEditor,
			cellRendererParams: {
				originalRenderer: VerticalNameCellRenderer,
				field:            `${level}Name`,
			},
			editable: editableOnThisLevel,
			level,
		},
	]), [level]);

	const fieldColumn = useMemo(() => (
		{
			cellStyle: (params: CellClassParams) => ({
				borderRightColor: params.data?.isFullRow ? 'transparent' : '',
				fontWeight:       params.data?.isFullRow ? 'bold' : '',
				color:            '#999999',
			}),
		}
	), []);

	const {
		onCellValueChanged: onCellValueChangedUndoRedo,
		onUndoStarted,
		onRedoStarted,
	} = useMutationsUndoRedoPlugin({ gridApi: api });

	const { onCellEditingStarted: onInstantEdit } = useInstantEditPlugin();

	const { columnDefs, rowData: transposedData } = useSupergridVerticalEngine({
		config,
		data:       mutations,
		addNameRow: false,
		level,
		fieldColumn,
		colDefs,
	});

	const parentMutations = mutations[
		getLevelByDepth(REDDIT_SUPERGRID_CONFIG, getLevelDepth(REDDIT_SUPERGRID_CONFIG, level) - 1)
	];

	const verticalColDefs = useMemo(() => insertAddColumns(
		columnDefs as VerticalSuperGridColDef<RedditLevel>[],
		mutations[level],
		parentMutations,
		level,
		RedditVerticalAddCellRenderer,
	), [columnDefs, level]);

	const [rowData, pinnedTopRowData] = useMemo(() => [
		transposedData.slice(level === RedditLevel.AD ? 2 : (level === RedditLevel.CAMPAIGN ? 0 : 1)),
		transposedData.slice(0, level === RedditLevel.AD ? 2 : (level === RedditLevel.CAMPAIGN ? 0 : 1)),
	], [transposedData]);

	const onGridReady = (e: GridReadyEvent) => {
		setColApi(e.columnApi);
		setApi(e.api);

		const firstColToBeFocused = e.columnApi?.getAllDisplayedColumns()?.[1]?.getColId();

		if (firstColToBeFocused) {
			focusSuperGridCell({ api: e.api, rowIndex: 0, field: firstColToBeFocused });
		}
	};

	const gridWidth = (() => {
		if (colApi) {
			return colApi.getAllDisplayedColumns()?.reduce(
				(accu, colDef) => accu + (colDef.getActualWidth() ?? 375),
				0,
			);
		}

		return 0;
	})();

	const onCellEditingStarted = (e: CellEditingStartedEvent) => {
		onCellEditingStartedColumnEditor(e);
		onInstantEdit(e);
	};

	const onCellValueChanged = async (e: CellValueChangedEvent) => {
		let newName: string | undefined;

		if (
			isNamingFormula(e.value)
            && e.data.field === 'name'
            && (e.source === 'edit' || e.source === 'paste')
		) {
			handleCellLoading([e.column], [e.node], true);

			try {
				const res = await transformNamingExpression([{
					data:       (e.colDef as VerticalSuperGridColDef).entityData,
					expression: e.value,
				}], client);
				[newName] = res;
			} catch (err) {
				console.error(err);
				newName = "Error: Couldn't generate name";
			}
			handleCellLoading([e.column], [e.node], false);
		}
		const entityData = (e.colDef as VerticalSuperGridColDef)?.entityData;
		onCellValueChangedUndoRedo?.(e);

		const entityKey = {
			id:   entityData?.id,
			hash: entityData?.hash,
		};

		const newData = {
			...entityKey,
			...entityData,
			[e.data?.field ?? '']: e.newValue,
		};

		changeMutationWithAutoName(newData, newName, e.value, e.data.field === 'name', changeMutation);

		const changedField = e.data?.colId ?? e.data?.field ?? '';

		const dependencies = REDDIT_CROSS_LEVEL_DEPENDENCIES[changedField]?.(e.newValue);
		const affectedChildrenHashes = updateAndReturnAffectedChildren({
			parentHash: entityKey?.hash,
			dependencies,
		}).map(child => child.hash);

		const dependenciesKeys = Object.keys(dependencies ?? {});

		if (changedField === 'campaignObjective') {
			const affectedGroups = mutations.group.filter(
				item => item.parent.hash === entityKey?.hash,
			).map(item => item.hash as string);

			affectedChildrenHashes.push(...affectedGroups);
			dependenciesKeys.push('groupBidAmount');
		}

		context.unsetValidations(dependenciesKeys, affectedChildrenHashes);
	};

	const hasValidationErrors = validations?.some(validation => validation.level === level);

	useEffect(() => {
		api?.refreshCells({ force: true });
	}, [validations]);

	const onCellKeyDown = (e: CellKeyDownEvent) => {
		if ((e.event as KeyboardEvent).key === 'Escape') {
			refocusCell(e);
		}
	};

	const isInvalidAdAccount = isPendingAdAccount(mutations[RedditCreateLevels.CAMPAIGN][0]?.parent)
     && level === RedditLevel.AD;

	const isInvalid = isInvalidAdAccount || hasValidationErrors;

	const gridOptions = useMemo(() => ({
		fillOperation: (params: FillOperationParams) => {
			const namingExpression = (params.api.getCellRanges()?.[0]?.startColumn?.getColDef() as VerticalSuperGridColDef)?.entityData?.namingExpression;

			return namingExpression || params.values[0];
		},
	}), []);

	return (
		<>
			<VerticalHeadlines api={api} />
			<ValidationNavigator
				isVertical
				gridApi={api}
				setLevel={setLevel}
				level={level}
				mappedFields={MAPPED_COLUMN_NAMES}
			/>

			<SuperGridVerticalAnchors
				config={REDDIT_SUPERGRID_CONFIG}
				topLevel={RedditLevel.CAMPAIGN}
				level={level}
				setLevel={onAnchorLevelChange}
				components={{
					anchorsContainer: NumberedAnchorsContainer,
					anchorBody:       (props) => (<NumberedAnchorsBody
						{...props}
						components={{
							anchorMain: (mainProps) => (<button {...mainProps.componentProps}
								className={classNames(mainProps.componentProps.className, 'font-bold')}
								data-testid={`anchor-${mainProps.componentProps.key}-button`}
							>
								{mainProps.children}
							</button>),
							anchorLabel: (labelProps) => (<div {...labelProps.componentProps}
								className='text-[12px]'
							>
								{labelProps.children}
							</div>),
						}}
					/>),
				}}
			/>

			<AnimatePresence mode="wait">
				<div
					key={level}
					className="mutator-grid ag-theme-alpine
					flex flex-row flex-1 justify-center overflow-y-auto mr-[-32px]"
				>
					<motion.div
						id="super-grid"
						transition={{ duration: 0.1 }}
						exit={{ filter: 'blur(10px)' }}
						initial={{ filter: 'blur(10px)' }}
						animate={{ filter: 'blur(0px)' }}
						className={classNames('flex-col flex', {
							'vertical-grid-scrolled': !showHeader,
						})}
						onWheel={e => {
							const columnEditor = document.getElementById('column-editor-wrapper');

							if (!columnEditor) {
								api?.stopEditing();
							}

							if (columnEditor?.contains(e.target as Node)) {
								return;
							}

							// ensure scroll is vertical
							if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
								setShowHeader(e.deltaY < 0);
							}
						}}
						style={{ flexBasis: gridWidth }}
					>
						<div
							className={classNames('text-[24px] flex items-center py-[36px] transition-all', {
								'max-h-0 !p-0': !showHeader,
							})}
						>
							{startCase(level)} Settings
						</div>

						<AgGridReact
							onUndoStarted={onUndoStarted}
							onRedoStarted={onRedoStarted}
							undoRedoCellEditing
							containerStyle={{
								// TODO: This has to be re-rendered when user changes window width
								marginRight: min([0, 16 - ((document.body.clientWidth - gridWidth) / 2)]),
							}}
							className="vertical-mutation-grid"
							suppressContextMenu
							enableRangeSelection
							suppressScrollOnNewData
							onGridReady={onGridReady}
							onCellValueChanged={onCellValueChanged}
							onCellKeyDown={onCellKeyDown}
							onCellFocused={e => e.api?.refreshCells({ force: true })}
							enableFillHandle
							headerHeight={0}
							rowClassRules={{
								'bg-white border-l border-l-gray-300 border-r border-r-gray-300': () => true,
								'border-t border-t-gray-300':
									(row) => row.rowIndex === 0,
								'!bg-gray-50 !text-[14px] !text-gray-600':
                                    params => params.data?.field === 'groupVerticalName'
                                        || params.data?.field === 'campaignVerticalName',
							}}
							rowBuffer={100}
							rowHeight={40}
							onRowDataUpdated={refocusCell}
							suppressColumnVirtualisation
							suppressRowTransform
							suppressPropertyNamesCheck
							rowData={rowData}
							context={{
								...context, client, isVertical: true, isInCreation: true,
							}}
							onCellEditingStarted={onCellEditingStarted}
							onCellEditingStopped={onCellEditingStopped}
							pinnedTopRowData={pinnedTopRowData}
							processCellFromClipboard={processValueFromJSON}
							processCellForClipboard={processValueToJSON}
							columnDefs={verticalColDefs}
							defaultColDef={DEFAULT_VERTICAL_COL_DEF}
							getRowHeight={(params: RowHeightParams) => (params.data?.field === 'adPreview' ? 250 : 40)}
							gridOptions={gridOptions}
						/>
					</motion.div>

					<Floaty>
						<Button
							onClick={clearMutations}
							leftNode={
								<TrashCanIcon />
							}
							appearance={ButtonAppearance.OUTLINED}
							color={ButtonColor.RED}
							data-testid="discard-changes-button"
							size={ButtonSize.LARGE}
						/>

						{level !== RedditLevel.CAMPAIGN
							&& (
								<Button
									data-testid="back-button"
									appearance={ButtonAppearance.OUTLINED}
									leftNode={<ArrowLeft />}
									onClick={onPreviousLevel}
									size={ButtonSize.LARGE}
								/>
							)
						}

						<Tooltip
							placement="top"
							content={t('sc: supergrid creation floaty there are errors message', 'There are errors')}
							disabled={validations.length === 0}
						>
							<Button
								title="Save draft"
								data-testid="save-draft-button"
								onClick={() => confirmMutations(api, 'PAUSED')}
								disabled={validations.length > 0}
								appearance={ButtonAppearance.OUTLINED}
								style={{ pointerEvents: hasValidationErrors ? 'none' : undefined }}
								size={ButtonSize.LARGE}
							/>
						</Tooltip>

						<Tooltip
							placement="top"
							content={
								isInvalidAdAccount
									? t(
										'sc: supergrid creation floaty disabled while pending advertiser verification message',
										'Disabled while pending Advertiser verification',
									)
									: t('sc: supergrid creation floaty there are errors message', 'There are errors')
							}
							disabled={!isInvalid}
							delay={300}
							singleLine
						>
							<Button
								onClick={onNextLevel}
								style={{ pointerEvents: isInvalid ? 'none' : undefined }}
								disabled={isInvalid}
								title={level !== RedditLevel.AD ? 'Next step' : 'Publish to Reddit'}
								data-testid={level !== RedditLevel.AD ? 'next-step-button' : 'publish-button'}
								rightNode={
									<DirectionRight
										width={16}
										height={16}
									/>
								}
								size={ButtonSize.LARGE}
							/>
						</Tooltip>
					</Floaty>
				</div>
			</AnimatePresence>
		</>
	);
};

export default RedditVerticalMutationGrid;
